import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ChannelDetail({ channel, onSettingsOpen, settingsOpen, onBack }) {
    const [activeTab, setActiveTab] = useState('overview');
    const [memberSearch, setMemberSearch] = useState('');

    const memberData = (channel.members || []).map(m => ({
        id: m._id || m.id,
        name: m.name || m.displayName || 'Member',
        role: m.role || 'Member',
        status: m.isLive ? 'online' : 'offline',
        isAdmin: channel.adminId === (m._id || m.id),
        joinedAt: m.createdAt || channel.createdAt,
        avatar: m.profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name || 'M')}&background=random`
    }));

    const filteredMembers = memberData.filter(m =>
        m.name.toLowerCase().includes(memberSearch.toLowerCase())
    );

    const statusColor = (status) => {
        if (status === 'online') return 'bg-green-500';
        if (status === 'away') return 'bg-yellow-400';
        return 'bg-gray-500';
    };

    const tabs = ['overview', 'members', 'files', 'pinned'];

    return (
        <div className="flex flex-col h-full bg-[#161614]">

            {/* HEADER */}
            <div className="flex justify-between items-center px-5 py-4 border-b border-white/10">

                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="md:hidden text-gray-400">←</button>

                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-500/10 text-stone-400">
                        #
                    </div>

                    <div>
                        <h2 className="text-white font-semibold">#{channel.name}</h2>
                        <p className="text-xs text-gray-400">{channel.description}</p>
                    </div>
                </div>

                <div className="flex gap-2">
                    <Link
                        to="/chat-interface"
                        className="hidden sm:block px-3 py-2 bg-stone-500/10 text-stone-400 rounded-lg text-sm"
                    >
                        Open Chat
                    </Link>

                    <button
                        onClick={onSettingsOpen}
                        className={`p-2 rounded-lg ${settingsOpen ? 'bg-stone-500/20 text-stone-400' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        ⚙
                    </button>
                </div>
            </div>

            {/* TABS */}
            <div className="flex border-b border-white/10 px-5">
                {tabs.map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-3 text-sm capitalize border-b-2 ${activeTab === tab
                            ? 'border-stone-500 text-stone-400'
                            : 'border-transparent text-gray-400'
                            }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto p-5">

                {/* OVERVIEW */}
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-2 gap-4">
                        <Stat label="Members" value={channel.memberCount} />
                        <Stat label="Messages" value={channel.messageCount} />
                        <Stat label="Created" value={channel.createdAt} />
                        <Stat label="Last Activity" value={channel.lastActivity} />
                    </div>
                )}

                {/* MEMBERS */}
                {activeTab === 'members' && (
                    <>
                        <input
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            placeholder="Search members..."
                            className="w-full mb-4 px-3 py-2 bg-[#232320] text-white rounded-lg"
                        />

                        {filteredMembers.map(member => (
                            <MemberRow key={member.id} member={member} statusColor={statusColor} />
                        ))}
                    </>
                )}

                {/* FILES */}
                {activeTab === 'files' && (
                    <div className="text-gray-400 text-sm">
                        File list coming soon...
                    </div>
                )}

                {/* PINNED */}
                {activeTab === 'pinned' && (
                    <div className="text-gray-400 text-sm">
                        Pinned messages coming soon...
                    </div>
                )}

            </div>
        </div>
    );
}

/* STAT CARD */
function Stat({ label, value }) {
    return (
        <div className="bg-[#232320] p-4 rounded-xl border border-white/10">
            <p className="text-white font-semibold">{value}</p>
            <p className="text-xs text-gray-400">{label}</p>
        </div>
    );
}

/* MEMBER ROW */
function MemberRow({ member, statusColor }) {
    return (
        <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">

            <div className="relative">
                <img src={member.avatar} className="w-9 h-9 rounded-full" alt="Member avatar" />
                <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border border-black ${statusColor(member.status)}`} />
            </div>

            <div className="flex-1">
                <p className="text-white text-sm">{member.name}</p>
                <p className="text-xs text-gray-400">{member.role}</p>
            </div>

            {member.isAdmin && (
                <span className="text-xs bg-green-500/10 text-green-400 px-2 py-0.5 rounded">
                    Admin
                </span>
            )}

        </div>
    );
}